import { useEffect, useState } from 'react';
import { Field } from '@/isomorphic/components/forms/components/field';
import { useAccountNumbeValidation } from '../hooks/use-account-number-validation';

type AccountValues = {
    bankName: string;
    clearingNumber: string;
    accountNumber: string;
};

type Props = {
    values: AccountValues;
    labels: AccountValues;
    hideBankNameField?: boolean;
    showRequiredError?: boolean;
    bankFieldDisabled?: boolean;
    onChange: (args: AccountValues) => void;
    accountNumberValidatedCallback: (isValidated: boolean) => void;
};

export const AccountClearingNumber = ({
    values,
    labels,
    onChange,
    hideBankNameField = false,
    bankFieldDisabled = false,
    showRequiredError = false,
    accountNumberValidatedCallback,
}: Props) => {
    const sanitizeNumber = (value: string) => value?.replace(/[^\d]+/g, '');
    const { validateAccountNumber, getBankName, validatingAccount, fetchingBankName } = useAccountNumbeValidation();

    const [hasRunInitialCheck, setHasRunInitialCheck] = useState(false);
    const [isAccountNumberValid, setIsAccountNumberValid] = useState(false);

    useEffect(() => {
        if (values.accountNumber && values.clearingNumber && !hasRunInitialCheck) {
            setHasRunInitialCheck(true);
            validateAccountDetails(values.accountNumber, values.clearingNumber);
        }
    }, [hasRunInitialCheck, values]);

    useEffect(() => {
        accountNumberValidatedCallback(isAccountNumberValid);
    }, [isAccountNumberValid]);

    async function fetchBankName(clearingNumber: string) {
        if (fetchingBankName) {
            return;
        }

        const apiBankNameResponse = await getBankName(sanitizeNumber(clearingNumber));
        onChange({ ...values, bankName: apiBankNameResponse ?? '' });
    }

    async function validateAccountDetails(accountNumber: string, clearingNumber: string) {
        if (validatingAccount) {
            return;
        }

        const { isValid = false } = await validateAccountNumber(sanitizeNumber(clearingNumber), sanitizeNumber(accountNumber));
        setIsAccountNumberValid(isValid);
    }

    const updateClearingNumber = (clearingNumber: string) => {
        let value = clearingNumber;
        if (value.length === 5 && /^\d+$/.test(value)) {
            value = value.slice(0, 4) + '-' + value.slice(4);
        }

        onChange({ ...values, clearingNumber });
    };

    const updateBankName = (bankName: string) => {
        if (bankFieldDisabled) {
            return;
        }

        onChange({ ...values, bankName });
    };

    return (
        <div>
            <Field
                grouped="top"
                value={values.clearingNumber}
                label={labels.clearingNumber}
                onChange={updateClearingNumber}
                validation={() => !!values.bankName}
                onBlur={clearingNumber => {
                    fetchBankName(clearingNumber);
                    validateAccountDetails(values.accountNumber, clearingNumber);
                }}
                showRequiredError={showRequiredError && (!values.clearingNumber || !values.bankName)}
            />
            <Field
                grouped={hideBankNameField ? 'bottom' : 'middle'}
                value={values.accountNumber}
                label={labels.accountNumber}
                validation={() => !!values.accountNumber && isAccountNumberValid}
                onChange={accountNumber => onChange({ ...values, accountNumber })}
                onBlur={accountNumber => validateAccountDetails(accountNumber, values.clearingNumber)}
                showRequiredError={showRequiredError && (!values.accountNumber || !isAccountNumberValid)}
            />
            {!hideBankNameField && (
                <Field
                    grouped="bottom"
                    value={values.bankName}
                    label={labels.bankName}
                    onChange={updateBankName}
                    disabled={bankFieldDisabled}
                    validation={() => !!values.bankName}
                    showRequiredError={showRequiredError && !values.bankName}
                />
            )}
        </div>
    );
};
